import { ChevronDown } from 'lucide-react'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { money } from '../../util/formatters'
import { Marketplaces } from '../../util/marketplaces'
import { getUpgradeItems } from './review-client'
import { DIFFERENCE_COPY, REVIEW_COPY, STATUS_COPY } from './review-copy'
import type {
    DifferenceClass,
    UpgradeItem,
    UpgradeItemsResponse,
    UpgradeReviewRow,
} from './review-types'

type ItemsState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'loaded'; data: UpgradeItemsResponse }

const VALUE_CLASSES = new Set<DifferenceClass>([
    'v2_safety_clamp',
    'deterministic_weighted_rounding_drift',
    'action_value_mismatch',
])

const valueFormatter = ({ value_kind, currency_code }: UpgradeItemsResponse) => {
    const marketplace =
        value_kind === 'money'
            ? Object.values(Marketplaces).find((m) => m.currency_code === currency_code)
            : undefined
    return (value: string | null | undefined) => {
        if (value == null) return '—'
        const amount = Number(value)
        return marketplace && value.trim() !== '' && Number.isFinite(amount)
            ? (money(amount, marketplace) ?? value)
            : value
    }
}

const ItemRow = ({
    item,
    formatValue,
}: {
    item: UpgradeItem
    formatValue?: (value: string | null | undefined) => string
}) => (
    <li className="flex gap-4 border-b border-[#E5E7EB] py-2 last:border-b-0">
        <div className="min-w-0 flex-1 break-words">
            {item.name ? (
                <div className="text-sm leading-[21px] text-[#1C2127]">{item.name}</div>
            ) : (
                <div className="text-sm leading-[21px]">
                    <span className="text-[#91969B]">{REVIEW_COPY.items.nameMissing}</span>{' '}
                    <span className="font-mono text-[13px] text-[#1C2127]">
                        {REVIEW_COPY.items.id(item.entity_id)}
                    </span>
                </div>
            )}
            {item.context && (
                <div className="text-xs leading-[18px] text-[#5F6B7C]">{item.context}</div>
            )}
        </div>
        {formatValue && (
            <span className="shrink-0 font-mono text-[13px] leading-[21px] text-[#1C2127]">
                {`${formatValue(item.v1)} → ${formatValue(item.v2)}`}
            </span>
        )}
    </li>
)

const ItemList = ({
    state,
    difference,
    onRetry,
}: {
    state: ItemsState | undefined
    difference: DifferenceClass
    onRetry: () => void
}) => {
    const message = 'px-6 pb-4 pl-12 text-[13px] leading-5'
    if (!state || state.status === 'loading')
        return (
            <div role="status" className={`${message} text-[#5F6B7C]`}>
                {REVIEW_COPY.items.loading}
            </div>
        )
    if (state.status === 'error')
        return (
            <div role="alert" className={`${message} text-[#AC2F33]`}>
                {REVIEW_COPY.items.error}
                <button type="button" onClick={onRetry} className="ml-2 underline">
                    {REVIEW_COPY.items.retry}
                </button>
            </div>
        )
    if (!state.data.items_available)
        return <div className={`${message} text-[#5F6B7C]`}>{REVIEW_COPY.items.unavailable}</div>
    const entry = state.data.classes.find((item) => item.class === difference)
    const items = entry?.items ?? []
    const count = entry?.count ?? 0
    const formatValue = VALUE_CLASSES.has(difference) ? valueFormatter(state.data) : undefined
    return (
        <div className="px-6 pb-4 pl-12">
            <ul className="m-0 max-h-[320px] list-none overflow-y-auto p-0">
                {items.map((item) => (
                    <ItemRow key={item.entity_id} item={item} formatValue={formatValue} />
                ))}
            </ul>
            {count > items.length && (
                <div className="pt-2 text-[13px] leading-5 text-[#5F6B7C]">
                    {REVIEW_COPY.items.truncated(items.length, count)}
                </div>
            )}
        </div>
    )
}

const formatCheckedAt = (checkedAt: string | null) => {
    if (!checkedAt) return null
    const date = new Date(checkedAt)
    return Number.isNaN(date.valueOf())
        ? null
        : `${date.getUTCDate()} ${date.toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' })}`
}

const CodePane = ({ label, subtitle, code }: { label: string; subtitle: string; code: string }) => (
    <section className="min-w-0 flex-1 overflow-hidden rounded-md bg-[#22212B] text-[#F8F8F2]">
        <header className="flex items-center gap-3 bg-white/[0.04] px-[14px] py-3">
            <span
                className={`rounded-sm px-2 py-0.5 text-xs ${label === 'Recipe Trigger' ? 'bg-[rgb(200_118_25_/_10%)] text-[#E0A458]' : 'bg-[rgb(35_133_81_/_10%)] text-[#5BC98E]'}`}
            >
                {label}
            </span>
            <span className="text-xs text-[#776EA3]">{subtitle}</span>
        </header>
        {/* Bootstrap styles `pre` with a dark colour, so set the text colour on the pre itself. */}
        <pre className="m-0 max-h-[480px] min-h-[112px] overflow-auto px-[14px] py-5 font-mono text-[13px] leading-6 text-[#F8F8F2]">
            {code || 'No Segment trigger is available.'}
        </pre>
    </section>
)

export const UpgradeReviewDetail = ({
    row,
    onUpgrade,
    upgrading,
    showUpgrade = true,
}: {
    row: UpgradeReviewRow
    onUpgrade: () => void
    upgrading: boolean
    showUpgrade?: boolean
}) => {
    const classes = row.diff_summary.classes || []
    const checked = formatCheckedAt(row.checked_at)
    const matches = row.diff_summary.rows_match || 0
    const total = row.diff_summary.rows_total || 0
    const differing = Math.max(0, total - matches)
    const canUpgrade = row.status === 'ok' || row.status === 'differences'
    const [itemsByCheck, setItemsByCheck] = useState<Record<string, ItemsState>>({})
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})

    const itemCacheKey = `${row.recipe_id}:${row.checked_at ?? ''}`
    const loadItems = () => {
        setItemsByCheck((current) => ({ ...current, [itemCacheKey]: { status: 'loading' } }))
        getUpgradeItems(row.recipe_id).then(
            (data) =>
                setItemsByCheck((current) => ({
                    ...current,
                    [itemCacheKey]: { status: 'loaded', data },
                })),
            () =>
                setItemsByCheck((current) => ({
                    ...current,
                    [itemCacheKey]: { status: 'error' },
                })),
        )
    }

    const toggle = (difference: DifferenceClass) => {
        const key = `${itemCacheKey}:${difference}`
        const opening = !expanded[key]
        setExpanded((current) => ({ ...current, [key]: opening }))
        const cached = itemsByCheck[itemCacheKey]
        if (!opening || (cached && cached.status !== 'error')) return
        loadItems()
    }

    return (
        <section
            aria-label="Selected Recipe details"
            className="overflow-hidden rounded border border-[#E5E7EB] bg-white"
        >
            <header className="flex min-h-[70px] items-center gap-5 border-b border-[#E5E7EB] px-[18px] py-[14px]">
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold tracking-[0.04em] text-[#91969B]">
                        SELECTED RECIPE
                    </div>
                    <div className="truncate text-base font-semibold leading-6 text-[#1C2127]">
                        {row.name}
                    </div>
                </div>
                <span className="rounded-sm bg-[rgb(200_118_25_/_10%)] px-1.5 py-0.5 text-xs leading-4 text-[#935610]">
                    {STATUS_COPY[row.status]}
                </span>
                {canUpgrade && showUpgrade && (
                    <button
                        type="button"
                        disabled={upgrading}
                        onClick={onUpgrade}
                        className="h-[30px] rounded-lg border border-[#DEE2E6] bg-white px-4 text-sm text-[#1C2127] shadow-[0_2px_4px_rgba(0,0,0,.075)] disabled:opacity-50"
                    >
                        Upgrade This Recipe
                    </button>
                )}
                {row.status === 'upgraded' && (
                    <Link to="/segments" className="text-sm text-[#215DB0]">
                        View Segment
                    </Link>
                )}
            </header>
            <div className="space-y-6 bg-[#F8FAFC] p-[18px]">
                {row.status === 'unparseable' ? (
                    <div
                        role="alert"
                        className="rounded-sm bg-[rgb(200_118_25_/_10%)] p-[15px] text-sm leading-[21px] text-[#935610]"
                    >
                        {REVIEW_COPY.unparseable}
                    </div>
                ) : row.status === 'pending' ? (
                    <div
                        role="status"
                        className="rounded-sm bg-[rgb(45_114_210_/_10%)] p-[15px] text-sm leading-[21px] text-[#215DB0]"
                    >
                        {REVIEW_COPY.pending}
                    </div>
                ) : (
                    <>
                        <div className="flex gap-6">
                            <CodePane
                                label="Recipe Trigger"
                                subtitle="Current"
                                code={row.v1_trigger}
                            />
                            <CodePane
                                label="Segment Trigger"
                                subtitle="After upgrading"
                                code={row.converted_trigger || ''}
                            />
                        </div>
                        <div className="overflow-hidden rounded border border-[#E5E7EB] bg-white">
                            <div className="bg-[rgb(200_118_25_/_10%)] px-6 py-4 text-[13px] text-[#935610]">
                                <span className="font-bold tracking-[0.04em]">
                                    {differing} DIFFERENCE{differing === 1 ? '' : 'S'}
                                </span>
                                {total > 0 && (
                                    <span className="ml-3">
                                        {REVIEW_COPY.itemsMatch(matches, total, [row.ad_type])}
                                        {checked && ` · ${REVIEW_COPY.checked(checked)}`}
                                    </span>
                                )}
                            </div>
                            {classes.length ? (
                                classes.map(({ class: difference, count }) => {
                                    const key = `${itemCacheKey}:${difference}`
                                    const open = Boolean(expanded[key])
                                    const listId = `upgrade-items-${row.recipe_id}-${difference}`
                                    if (count <= 0)
                                        return (
                                            <div
                                                key={difference}
                                                className="border-t border-[#E5E7EB] px-6 py-4 text-sm leading-[21px] text-[#1C2127]"
                                            >
                                                <span className="mr-2 font-medium">{count}</span>
                                                {DIFFERENCE_COPY[difference](count)}
                                            </div>
                                        )
                                    return (
                                        <div key={difference} className="border-t border-[#E5E7EB]">
                                            <button
                                                type="button"
                                                aria-expanded={open}
                                                aria-controls={open ? listId : undefined}
                                                onClick={() => toggle(difference)}
                                                className="flex w-full items-start gap-2 bg-white px-6 py-4 text-left text-sm leading-[21px] text-[#1C2127]"
                                            >
                                                <ChevronDown
                                                    aria-hidden="true"
                                                    className={`mt-0.5 h-4 w-4 shrink-0 text-[#5F6B7C] transition-transform ${open ? 'rotate-180' : ''}`}
                                                />
                                                <span className="mr-2 font-medium">{count}</span>
                                                {DIFFERENCE_COPY[difference](count)}
                                            </button>
                                            {open && (
                                                <div id={listId}>
                                                    <ItemList
                                                        state={itemsByCheck[itemCacheKey]}
                                                        difference={difference}
                                                        onRetry={loadItems}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="px-6 py-4 text-sm text-[#5F6B7C]">
                                    No differences found.
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    )
}
